/**
 * React integration for feature flags.
 *
 * Wrap the app in <FlagsProvider> and read flags with useFlag / useFlags. All
 * hooks are safe to call without PostHog credentials — they simply return the
 * fallback value.
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { createFlagsClient, noopClient, type FlagsClient } from "./client";

export interface FlagsProviderProps {
  children: ReactNode;
  client?: FlagsClient;
}

interface FlagsContextValue {
  client: FlagsClient;
  /** Bumped whenever the client re-resolves flags (e.g. PostHog loaded). */
  version: number;
}

const FlagsContext = createContext<FlagsContextValue>({
  client: noopClient,
  version: 0,
});

/**
 * Lazily-created default client. `createFlagsClient` calls `posthog.init` (a
 * global side effect), so it must never run at module import time nor inside a
 * component render — the provider creates it from a mount effect instead.
 * Without a PostHog key this is a no-op client, so it never throws.
 */
let defaultClientInstance: FlagsClient | undefined;
function getDefaultFlagsClient(): FlagsClient {
  defaultClientInstance ??= createFlagsClient();
  return defaultClientInstance;
}

export function FlagsProvider({ children, client }: FlagsProviderProps) {
  // The default client (if needed) is created in the effect below — never
  // during render. Until it exists consumers see the no-op client, which
  // simply returns fallback values. An explicitly-passed client always wins.
  const [defaultClient, setDefaultClient] = useState<FlagsClient | null>(null);
  const [version, setVersion] = useState(0);
  const resolved = client ?? defaultClient ?? noopClient;

  // StrictMode-safe: `getDefaultFlagsClient` is a module-level singleton, so
  // a double-invoked effect creates the client at most once.
  useEffect(() => {
    if (!client && !defaultClient) setDefaultClient(getDefaultFlagsClient());
  }, [client, defaultClient]);

  // Re-render consumers when PostHog resolves its flags asynchronously
  // (the `featureflags` event). No-op clients never fire, so this is a no-op
  // without credentials.
  useEffect(() => {
    if (!resolved.enabled) return undefined;
    return resolved.onFeatureFlags(() => setVersion((v) => v + 1));
  }, [resolved]);

  const value = useMemo(
    () => ({ client: resolved, version }),
    [resolved, version],
  );
  return (
    <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>
  );
}

export function useFlagsClient(): FlagsClient {
  return useContext(FlagsContext).client;
}

/**
 * Read a boolean feature flag with a safe fallback. Never throws, even when
 * PostHog is not configured. Re-evaluates when PostHog finishes loading
 * flags (via the provider's version counter).
 */
export function useFlag(key: string, fallback = false): boolean {
  const { client, version } = useContext(FlagsContext);
  return useMemo(
    () => client.isFeatureEnabled(key, fallback),
    // `version` re-runs the read when flags resolve.
    [client, key, fallback, version],
  );
}

/** Read several boolean flags at once. */
export function useFlags(keys: string[]): Record<string, boolean> {
  const { client, version } = useContext(FlagsContext);
  // Memoize on a string identity, not the array identity — callers commonly
  // pass inline literals which would otherwise re-memoize every render.
  const key = keys.join("\u0000");
  return useMemo(() => {
    const out: Record<string, boolean> = {};
    if (key === "") return out;
    for (const k of key.split("\u0000")) {
      out[k] = client.isFeatureEnabled(k, false);
    }
    return out;
    // `version` re-runs the read when flags resolve.
  }, [client, key, version]);
}

export type { FlagsClient, PostHogFlagsOptions } from "./client";
